import React, { useState } from 'react';
import localeConfig from '../config/locale.config';
import { formatDateTime } from '../utils/helpers';

const ServiceDetailsModal = ({ service, isOpen, onClose }) => {
    const [activeTab, setActiveTab] = useState('overview');

    if (!service || !isOpen) return null;

    const tabs = [
        { id: 'overview', label: 'Overview', icon: '📋' },
        { id: 'customer', label: 'Customer', icon: '👤' },
        { id: 'vehicle', label: 'Vehicle', icon: '🚗' },
        { id: 'location', label: 'Location', icon: '📍' },
        { id: 'timeline', label: 'Timeline', icon: '⏱️' },
        { id: 'documents', label: 'Documents', icon: '📄' }
    ];

    const getStatusColor = (status) => {
        const statusColors = {
            'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
            'assigned': 'bg-blue-100 text-blue-800 border-blue-200',
            'driver_started': 'bg-indigo-100 text-indigo-800 border-indigo-200',
            'vendor_reached_location': 'bg-purple-100 text-purple-800 border-purple-200',
            'photos_submitted': 'bg-orange-100 text-orange-800 border-orange-200',
            'service_resolved': 'bg-teal-100 text-teal-800 border-teal-200',
            'service_complete': 'bg-green-100 text-green-800 border-green-200',
            'otw_service_center': 'bg-cyan-100 text-cyan-800 border-cyan-200',
            'reached_service_center': 'bg-pink-100 text-pink-800 border-pink-200',
            'svc_photos_submitted': 'bg-amber-100 text-amber-800 border-amber-200',
            'service_cancelled': 'bg-red-100 text-red-800 border-red-200',
            'vendor_not_accepted': 'bg-gray-100 text-gray-800 border-gray-200'
        };
        return statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
    };

    const formatDate = (dateString) => formatDateTime(dateString);

    const renderOverview = () => (
        <div className="space-y-6">
            {/* Service Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 md:p-6 border border-blue-100">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <h3 className="text-xl md:text-2xl font-bold text-gray-900">{service.service_number}</h3>
                        <p className="text-gray-600 mt-1">{service.service_type}</p>
                    </div>
                    <div className="sm:text-right">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(service.status)}`}>
                            {service.status.replace(/_/g, ' ').toUpperCase()}
                        </span>
                        <p className="text-sm text-gray-500 mt-1">Created {formatDate(service.created_at)}</p>
                    </div>
                </div>
            </div>

            {/* Key Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                <span className="text-blue-600 text-sm font-medium">💰</span>
                            </div>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">Estimated Cost</p>
                            <p className="text-lg font-semibold text-gray-900">
                                {localeConfig.currency.symbol}{service.service?.estimated_cost || 'N/A'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                                <span className="text-green-600 text-sm font-medium">⏱️</span>
                            </div>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">Estimated Time</p>
                            <p className="text-lg font-semibold text-gray-900">
                                {service.service?.estimated_time_minutes || 'N/A'} min
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                <span className="text-purple-600 text-sm font-medium">📏</span>
                            </div>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-500">Distance</p>
                            <p className="text-lg font-semibold text-gray-900">
                                {service.service?.distance_km || 'N/A'} km
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Service Description */}
            {service.service?.service_description && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Service Description</h4>
                    <p className="text-gray-700 leading-relaxed">{service.service.service_description}</p>
                </div>
            )}

            {/* Notes */}
            {service.service?.notes && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Notes</h4>
                    <p className="text-gray-700 leading-relaxed">{service.service.notes}</p>
                </div>
            )}
        </div>
    );

    const renderCustomer = () => (
        <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Name</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">{service.customer}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Phone</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">{service.phone}</p>
                    </div>
                    {service.service?.case?.customer && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-500">Email</label>
                                <p className="mt-1 text-lg font-semibold text-gray-900">
                                    {service.service.case.customer.email || 'N/A'}
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500">Address</label>
                                <p className="mt-1 text-lg font-semibold text-gray-900">
                                    {service.service.case.customer.address || 'N/A'}
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {service.service?.case && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Case Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-500">Case Number</label>
                            <p className="mt-1 text-lg font-semibold text-gray-900">{service.service.case.case_number}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-500">Case Status</label>
                            <p className="mt-1 text-lg font-semibold text-gray-900">{service.service.case.status}</p>
                        </div>
                        {service.service.case.scheme_cause_of_call && (
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-500">Cause of Call</label>
                                <p className="mt-1 text-lg font-semibold text-gray-900">
                                    {service.service.case.scheme_cause_of_call.name}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    const renderVehicle = () => (
        <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Vehicle Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Registration Number</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                            {service.service?.case?.vehicle?.registration_number || 'N/A'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Make & Model</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                            {service.service?.case?.vehicle?.make} {service.service?.case?.vehicle?.model}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Color</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                            {service.service?.case?.vehicle?.color || 'N/A'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Fuel Type</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                            {service.service?.case?.vehicle?.fuel_type || 'N/A'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Year</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                            {service.service?.case?.vehicle?.year || 'N/A'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Chassis Number</label>
                        <p className="mt-1 text-sm font-semibold text-gray-900 break-all">
                            {service.service?.case?.vehicle?.chassis_number || 'N/A'}
                        </p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Service Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Service Type</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">{service.service_type}</p>
                    </div>
                    {service.scheme_service_subtype && (
                        <div>
                            <label className="block text-sm font-medium text-gray-500">Service Subtype</label>
                            <p className="mt-1 text-lg font-semibold text-gray-900">{service.scheme_service_subtype.name}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderLocation = () => (
        <div className="space-y-6">
            {/* Pickup Location */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">📍</span>
                    Pickup Location
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Address</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                            {service.pickup_location?.address || 'N/A'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500">City</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                            {service.pickup_location?.city || 'N/A'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500">{localeConfig.address.regionLabel}</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                            {service.pickup_location?.state || 'N/A'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Landmark</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                            {service.pickup_location?.landmark || 'N/A'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Coordinates</label>
                        <p className="mt-1 text-sm font-semibold text-gray-900">
                            {service.pickup_location?.latitude && service.pickup_location?.longitude 
                                ? `${service.pickup_location.latitude}, ${service.pickup_location.longitude}`
                                : 'N/A'
                            }
                        </p>
                    </div>
                </div>
            </div>

            {/* Drop Location (if exists) */}
            {service.service?.drop_location && Object.keys(service.service.drop_location).length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <span className="mr-2">🏁</span>
                        Drop Location
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-500">Address</label>
                            <p className="mt-1 text-lg font-semibold text-gray-900">
                                {service.service.drop_location.address || 'N/A'}
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-500">City</label>
                            <p className="mt-1 text-lg font-semibold text-gray-900">
                                {service.service.drop_location.city || 'N/A'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Operating Location */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">🏢</span>
                    Operating Location
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Location</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">{service.operating_location}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Type</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                            {service.vendor_location?.vendor_location_type || 'N/A'}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderTimeline = () => (
        <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-6">Service Timeline</h4>
                <div className="space-y-4">
                    {service.service?.created_at && (
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <span className="text-blue-600 text-sm">📝</span>
                                </div>
                            </div>
                            <div className="ml-4 flex-1">
                                <p className="text-sm font-medium text-gray-900">Service Created</p>
                                <p className="text-sm text-gray-500">{formatDate(service.service.created_at)}</p>
                            </div>
                        </div>
                    )}
                    
                    {service.service?.assigned_at && (
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                    <span className="text-green-600 text-sm">👤</span>
                                </div>
                            </div>
                            <div className="ml-4 flex-1">
                                <p className="text-sm font-medium text-gray-900">Driver Assigned</p>
                                <p className="text-sm text-gray-500">{formatDate(service.service.assigned_at)}</p>
                            </div>
                        </div>
                    )}

                    {service.service?.started_at && (
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                                    <span className="text-orange-600 text-sm">🚀</span>
                                </div>
                            </div>
                            <div className="ml-4 flex-1">
                                <p className="text-sm font-medium text-gray-900">Service Started</p>
                                <p className="text-sm text-gray-500">{formatDate(service.service.started_at)}</p>
                            </div>
                        </div>
                    )}

                    {service.service?.reached_at && (
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                    <span className="text-purple-600 text-sm">📍</span>
                                </div>
                            </div>
                            <div className="ml-4 flex-1">
                                <p className="text-sm font-medium text-gray-900">Reached Location</p>
                                <p className="text-sm text-gray-500">{formatDate(service.service.reached_at)}</p>
                            </div>
                        </div>
                    )}

                    {service.service?.service_started_at && (
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                                    <span className="text-teal-600 text-sm">🔧</span>
                                </div>
                            </div>
                            <div className="ml-4 flex-1">
                                <p className="text-sm font-medium text-gray-900">Work Started</p>
                                <p className="text-sm text-gray-500">{formatDate(service.service.service_started_at)}</p>
                            </div>
                        </div>
                    )}

                    {service.service?.completed_at && (
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                    <span className="text-green-600 text-sm">✅</span>
                                </div>
                            </div>
                            <div className="ml-4 flex-1">
                                <p className="text-sm font-medium text-gray-900">Service Completed</p>
                                <p className="text-sm text-gray-500">{formatDate(service.service.completed_at)}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderDocuments = () => (
        <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Documents & Attachments</h4>
                                 <div className="space-y-4">
                     <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                         <div className="flex items-center">
                             <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                 <span className="text-green-600">📸</span>
                             </div>
                             <div className="ml-3">
                                 <p className="text-sm font-medium text-gray-900">Service Photos</p>
                                 <p className="text-sm text-gray-500">Before and after service photos</p>
                             </div>
                         </div>
                         <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                             View Photos
                         </button>
                     </div>
                </div>
            </div>
        </div>
    );

    const renderTabContent = () => {
        switch (activeTab) {
            case 'overview':
                return renderOverview();
            case 'customer':
                return renderCustomer();
            case 'vehicle':
                return renderVehicle();
            case 'location':
                return renderLocation();
            case 'timeline':
                return renderTimeline();
            case 'documents':
                return renderDocuments();
            default:
                return renderOverview();
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end sm:items-center justify-center min-h-screen pt-0 sm:pt-4 px-0 sm:px-4 pb-0 sm:pb-20 text-center sm:block sm:p-0">
                {/* Background overlay */}
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

                {/* Modal panel */}
                <div className="inline-block align-bottom bg-white rounded-t-lg sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full max-h-[95vh] sm:max-h-[90vh] flex flex-col">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 md:px-6 py-4 shrink-0">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center min-w-0">
                                <h2 className="text-lg md:text-xl font-semibold text-white">Service Details</h2>
                                <span className="ml-2 md:ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white bg-opacity-20 text-white truncate">
                                    {service.service_number}
                                </span>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-white hover:text-gray-200 transition-colors shrink-0"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="border-b border-gray-200 shrink-0 overflow-x-auto">
                        <nav className="flex space-x-4 md:space-x-8 px-4 md:px-6 min-w-max" aria-label="Tabs">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`py-3 md:py-4 px-1 border-b-2 font-medium text-sm flex items-center whitespace-nowrap ${
                                        activeTab === tab.id
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    <span className="mr-2">{tab.icon}</span>
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Content */}
                    <div className="px-4 md:px-6 py-4 md:py-6 overflow-y-auto flex-1">
                        {renderTabContent()}
                    </div>

                                         {/* Footer */}
                     <div className="bg-gray-50 px-4 md:px-6 py-3 flex justify-end shrink-0">
                         <button
                             onClick={onClose}
                             className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                         >
                             Close
                         </button>
                     </div>
                </div>
            </div>
        </div>
    );
};

export default ServiceDetailsModal;
